/**
 * Robust API error message extraction.
 *
 * The api/provider.js wrapper rejects with `error.response` (see api/response.js
 * handleError), so callers usually receive the axios response object directly
 * ({ status, data, ... }). This helper also tolerates raw axios errors (with a
 * nested `.response`) and plain Errors.
 *
 * It flattens the many shapes a FastAPI/Pydantic backend can return so the UI
 * never displays raw JSON to the user:
 *   - "a plain string"
 *   - { detail: "a message" }
 *   - { detail: [{ msg, loc, type }, ...] }   (422 validation errors)
 *   - { detail: { message | msg: "..." } }
 *   - { message: "a message" }
 */

/**
 * Turn a single validation-error entry into a readable sentence.
 * @param {*} entry
 * @returns {string}
 */
function formatValidationEntry(entry) {
  if (typeof entry === 'string') return entry;
  if (entry && typeof entry === 'object') {
    return entry.msg || entry.message || '';
  }
  return '';
}

/**
 * Extract a human-readable message from an API error payload (`data`).
 * @param {*} data
 * @returns {string|null} message, or null if nothing usable was found
 */
function messageFromData(data) {
  if (data == null) return null;
  if (typeof data === 'string') return data.trim() || null;

  if (Array.isArray(data)) {
    const parts = data.map(formatValidationEntry).filter(Boolean);
    return parts.length ? parts.join('. ') : null;
  }

  if (typeof data === 'object') {
    const { detail } = data;
    if (typeof detail === 'string') return detail.trim() || null;
    if (Array.isArray(detail)) {
      const parts = detail.map(formatValidationEntry).filter(Boolean);
      if (parts.length) return parts.join('. ');
    }
    if (detail && typeof detail === 'object') {
      const nested = detail.message || detail.msg;
      if (typeof nested === 'string' && nested.trim()) return nested.trim();
    }
    if (typeof data.message === 'string' && data.message.trim()) return data.message.trim();
  }

  return null;
}

/**
 * Extract a clean, user-facing error message from any error shape.
 * @param {*} error - response object, axios error, or Error
 * @param {string} [fallback] - returned when no message can be extracted
 * @returns {string}
 */
export function extractApiError(error, fallback = 'Something went wrong. Please try again.') {
  if (!error) return fallback;

  // handleError rejects with `error.response`, so `error.data` is the payload.
  // Also support a raw axios error that still has a nested `.response`.
  const data = error.data ?? error.response?.data;
  const fromData = messageFromData(data);
  if (fromData) return fromData;

  if (typeof error.message === 'string' && error.message.trim()) return error.message.trim();

  return fallback;
}
